<script lang="ts">
  import {
    CheckboxesField,
    PhoneField,
    RadioButtonsField,
    MultiSelectField,
    SelectField,
    TextareaField,
    TextField,
    Text,
  } from '@squareup/public-web-design-system';
  import { type ColorMode } from '@squareup/public-web-design-system';
  import countryDetectionHelper from '$lib/helpers/country-detection-helper';
  // Import FlagIcon components for the PhoneField component
  import USFlag from './FlagIcon/Icons/USFlag.svelte';
  import GBFlag from './FlagIcon/Icons/GBFlag.svelte';
  import AUFlag from './FlagIcon/Icons/AUFlag.svelte';
  import CAFlag from './FlagIcon/Icons/CAFlag.svelte';
  import JPFlag from './FlagIcon/Icons/JPFlag.svelte';
  import IEFlag from './FlagIcon/Icons/IEFlag.svelte';
  import ESFlag from './FlagIcon/Icons/ESFlag.svelte';
  import FRFlag from './FlagIcon/Icons/FRFlag.svelte';
  import { localizedCountryNamesStore } from './FlagIcon/store';
  import { FLAG_ICON_COUNTRY_CODES } from './FlagIcon/types';
  import type { FlagIconProps } from './FlagIcon/types';
  import type { FormField } from '../types';

  export let colorMode: ColorMode;
  export let field: FormField;
  // inputValue in VisibleField is any, but all PWDS primitive only accept string except for CheckboxesField accepts
  // string[]
  export let inputValue: any = '';
  export let disabled: boolean;
  export let localizedCountryNames: Record<string, string>;
  export let locale: string = '';
  export let isError: boolean = false;

  const countryCode = countryDetectionHelper.extractCountryCodeFromLocale(locale);

  const dialCodes: Record<FlagIconProps['countryCode'], string> = {
    us: '+1',
    gb: '+44',
    au: '+61',
    ca: '+1',
    jp: '+81',
    ie: '+353',
    es: '+34',
    fr: '+33',
  };

  // Component type is temporarily set to any to avoid type errors
  // TODO: Update type to Component once PWDS is migrated to svelte 5
  const flagComponentMap: Record<FlagIconProps['countryCode'], any> = {
    us: USFlag,
    gb: GBFlag,
    au: AUFlag,
    ca: CAFlag,
    jp: JPFlag,
    ie: IEFlag,
    es: ESFlag,
    fr: FRFlag,
  };

  const placeholders: Record<FlagIconProps['countryCode'], string> = {
    us: '000 000 0000',
    gb: '0000 000000',
    au: '0000-000-000',
    ca: '000 000 0000',
    jp: '00 0000 0000',
    ie: '0000000000',
    es: '000000000',
    fr: '00 00 00 00 00',
  };

  const phoneSelectItems = FLAG_ICON_COUNTRY_CODES.map((code) => {
    // Update the store with localizedCountryName, so FlagIcon can have the correct alt attribute
    localizedCountryNamesStore.set(localizedCountryNames);

    return {
      label: localizedCountryNames[code],
      value: dialCodes[code],
      icon: flagComponentMap[code],
      placeholder: placeholders[code],
      selected: code === countryCode,
    };
  });
</script>

<div class="PwdsVisibleField">
  {#if field.dataType === 'textArea'}
    <TextareaField
      {colorMode}
      id={field.id}
      name={field.id}
      label={field.label}
      {disabled}
      placeholder={field.placeholder}
      required={field.required}
      pattern={field.pattern}
      validationMessage={field.validationMessage ?? undefined}
      autocomplete={field.autocompleteType ?? undefined}
      bind:value={inputValue}
      bind:invalid={isError}
    />
  {:else if field.dataType === 'select'}
    {#if field.multiSelect}
      <MultiSelectField
        id={field.id}
        placeholder={field.placeholder}
        {colorMode}
        label={field.label}
        name={field.id}
        {disabled}
        required={field.required}
        items={field.selectOptions.filter((option) => option.value !== '')}
        validationMessage={field.validationMessage ?? undefined}
        bind:selectedValues={inputValue}
        bind:invalid={isError}
      />
    {:else}
      <SelectField
        {colorMode}
        label={field.label}
        name={field.id}
        {disabled}
        required={field.required}
        items={field.selectOptions}
        validationMessage={field.validationMessage ?? undefined}
        bind:selectedValue={inputValue}
        bind:invalid={isError}
      />
    {/if}
  {:else if field.dataType === 'telephone'}
    <PhoneField
      {colorMode}
      id={field.id}
      name={field.id}
      label={field.label}
      {disabled}
      required={field.required}
      validationMessage={field.validationMessage ?? undefined}
      items={phoneSelectItems}
      stripDelimiters={true}
      bind:inputValue
      bind:invalid={isError}
    />
  {:else if field.dataType === 'checkboxes'}
    <CheckboxesField
      {colorMode}
      label={field.label}
      name={field.id}
      {disabled}
      required={field.required}
      items={field.selectOptions}
      validationMessage={field.validationMessage ?? undefined}
      bind:selectedValues={inputValue}
      bind:invalid={isError}
    />
  {:else if field.dataType === 'radioButtons'}
    <RadioButtonsField
      {colorMode}
      label={field.label}
      name={field.id}
      {disabled}
      required={field.required}
      items={field.selectOptions}
      validationMessage={field.validationMessage ?? undefined}
      bind:selectedValue={inputValue}
      bind:invalid={isError}
    />
  {:else if field.dataType === 'htmltext'}
    <!-- For static text that is configured as part of the form in Marketo, e.g. privacy notices -->
    <Text variant="50-r" data-color-mode={colorMode}>{@html field.text}</Text>
  {:else}
    <TextField
      {colorMode}
      id={field.id}
      name={field.id}
      label={field.label}
      {disabled}
      placeholder={field.placeholder}
      required={field.required}
      pattern={field.pattern}
      validationMessage={field.validationMessage ?? undefined}
      autocomplete={field.autocompleteType ?? undefined}
      bind:value={inputValue}
      bind:invalid={isError}
    />
  {/if}
</div>

<style lang="scss">
  @layer standard-components {
    .PwdsVisibleField {
      width: 100%;
      margin-bottom: var(--space-20);
      // For some reason there is negative margin applied to the right and left sides of the element with the class row
      padding: {
        right: var(--space-10);
        left: var(--space-10);
      }

      :global {
        .Text {
          &,
          &[data-color-mode='light'] {
            --color: var(--color-neutral-400);
          }

          &[data-color-mode='dark'] {
            --color: var(--color-neutral-600);
          }

          a {
            font-weight: var(--font-weight-regular);
            text-decoration: underline;

            &,
            &:hover {
              color: var(--color);
            }
          }
        }
      }
    }
  }
</style>
